import { FC, useEffect, useMemo, useState } from 'react';
import {
  App,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Typography
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { _debounce } from '@medicusunion/front-utils';
import {
  createCustomer,
  fetchCustomers,
  updateCustomer
} from 'services/api/invoicingCustomers';
import { Customer } from 'types/api/invoicing';
import { GENDERS } from 'constants/users';
import { Gender } from 'types/api/users';

import { useCountryOptions } from '../../../useCountryOptions';
import { customerAddressSummary } from '../../../customerDisplay';
import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  open: boolean;
  // When set, edit this customer (PATCH); otherwise find-or-create.
  editing?: Customer | null;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

type FormValues = {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: Dayjs;
  gender?: Gender;
  company?: string;
  vatId?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

const toFormValues = (c: Customer): FormValues => ({
  firstName: c.firstName,
  lastName: c.lastName ?? undefined,
  email: c.email ?? undefined,
  phone: c.phone ?? undefined,
  dateOfBirth: c.dateOfBirth ? dayjs(c.dateOfBirth) : undefined,
  gender: c.gender ?? undefined,
  company: c.company ?? undefined,
  vatId: c.vatId ?? undefined,
  line1: c.line1 ?? undefined,
  line2: c.line2 ?? undefined,
  city: c.city ?? undefined,
  state: c.state ?? undefined,
  postalCode: c.postalCode ?? undefined,
  country: c.country ?? undefined
});

const toInput = (values: FormValues) => ({
  firstName: values.firstName.trim(),
  lastName: values.lastName?.trim() || null,
  email: values.email?.trim() || null,
  phone: values.phone?.trim() || null,
  // Required on create, never forced on update — an edit of a customer
  // stored before these columns existed must not be blocked by them.
  dateOfBirth: values.dateOfBirth?.format('YYYY-MM-DD'),
  gender: values.gender,
  company: values.company?.trim() || null,
  vatId: values.vatId?.trim() || null,
  line1: values.line1?.trim() || null,
  line2: values.line2?.trim() || null,
  city: values.city?.trim() || null,
  state: values.state?.trim() || null,
  postalCode: values.postalCode?.trim() || null,
  country: values.country?.trim().toUpperCase() || null
});

/**
 * Find, create, or edit a non-user billing customer. A customer carries its own
 * billing address (the BE snapshots it onto the invoice). Returns the full
 * customer via onSelect.
 */
const CustomerModal: FC<IProps> = ({ open, editing, onClose, onSelect }) => {
  const [form] = Form.useForm<FormValues>();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const countryOptions = useCountryOptions();
  const isEdit = !!editing;

  // Existing-customer typeahead (select mode only).
  const [existing, setExisting] = useState<Customer | null>(null);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);

  const debounce = useMemo(() => _debounce(), []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setExisting(null);
    setQuery('');
    setRows([]);

    if (editing) {
      form.setFieldsValue(toFormValues(editing));
    } else {
      form.resetFields();
    }
  }, [open, editing, form]);

  useEffect(() => {
    if (!open || isEdit || !query) {
      return;
    }

    let cancelled = false;
    setSearching(true);

    fetchCustomers(query)
      .then(res => {
        if (!cancelled) setRows(res);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isEdit, query]);

  const options = rows.map(row => ({
    value: row.id,
    label: `${row.firstName} ${row.lastName ?? ''}${
      row.email ? ` (${row.email})` : ''
    }`.trim()
  }));

  const onPickExisting = (id?: string) => {
    setExisting(rows.find(row => row.id === id) ?? null);
  };

  const onFinish = async (values: FormValues) => {
    try {
      setLoading(true);

      let customer: Customer;

      if (editing) {
        customer = await updateCustomer(editing.id, toInput(values));
      } else if (existing) {
        customer = existing;
      } else {
        const { dateOfBirth, gender } = values;

        // Required by the form in create mode. Checked again so the type
        // stays honest and a removed rule fails loudly instead of 400ing.
        if (!dateOfBirth || !gender) {
          message.error('Date of birth and gender are required.');

          return;
        }

        customer = await createCustomer({
          ...toInput(values),
          dateOfBirth: dateOfBirth.format('YYYY-MM-DD'),
          gender
        });
      }

      onSelect(customer);
      onClose();
    } catch (err) {
      // Duplicate email → guide the admin to pick the existing customer.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((err as any)?.response?.status === 409) {
        message.warning(
          'A customer with this email already exists — search for it above and pick it.'
        );
      } else {
        showErrorMessage(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // In select mode a picked existing customer is used as-is (no address entry).
  const showFields = isEdit || !existing;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? 'Save changes' : 'Use customer'}
      title={isEdit ? 'Edit customer' : 'Bill a customer'}
      confirmLoading={loading}
      closable={!loading}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {!isEdit && (
          <>
            <Form.Item label="Find existing customer (optional)">
              <Select
                showSearch
                allowClear
                filterOption={false}
                placeholder="Search by name or email"
                options={options}
                loading={searching}
                onSearch={input => debounce(() => setQuery(input))}
                onChange={onPickExisting}
                notFoundContent={searching ? <Spin size="small" /> : null}
              />
            </Form.Item>

            {existing ? (
              <Typography.Paragraph type="secondary">
                Using{' '}
                <strong>{`${existing.firstName} ${
                  existing.lastName ?? ''
                }`}</strong>
                . Their saved billing address is used on the invoice
                {customerAddressSummary(existing)
                  ? ` (${customerAddressSummary(existing)}).`
                  : '.'}
              </Typography.Paragraph>
            ) : (
              <Typography.Text type="secondary">
                Or create a new customer:
              </Typography.Text>
            )}
          </>
        )}

        {showFields && (
          <>
            <Form.Item
              label="First name"
              name="firstName"
              rules={[{ required: true }]}
              style={{ marginTop: isEdit ? 0 : 12 }}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Last name" name="lastName">
              <Input />
            </Form.Item>
            <Form.Item label="Email" name="email" rules={[{ type: 'email' }]}>
              <Input placeholder="Needed to email the PDF" />
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input />
            </Form.Item>
            {/* The BE requires both when creating and never on update, so an
                older customer can still be edited without supplying them. */}
            <Form.Item
              label="Date of birth"
              name="dateOfBirth"
              rules={[{ required: !isEdit }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={current => current && current > dayjs()}
                placeholder="YYYY-MM-DD"
              />
            </Form.Item>
            <Form.Item
              label="Gender"
              name="gender"
              rules={[{ required: !isEdit }]}
            >
              <Select placeholder="Select gender" options={GENDERS} />
            </Form.Item>

            <Divider>Billing address</Divider>

            <Form.Item label="Company" name="company">
              <Input />
            </Form.Item>
            <Form.Item label="VAT ID" name="vatId">
              <Input />
            </Form.Item>
            <Form.Item
              label="Address line 1"
              name="line1"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Address line 2" name="line2">
              <Input />
            </Form.Item>
            <Form.Item label="City" name="city" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="State / region" name="state">
              <Input />
            </Form.Item>
            <Form.Item label="Postal code" name="postalCode">
              <Input />
            </Form.Item>
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select country"
                options={countryOptions}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default CustomerModal;
